import { type MouseEvent, useCallback, useEffect, useRef, useState } from "react";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { useRosterLookup } from "../../hooks/useRosterLookup";
import type { NurseInShift } from "../../utils/models";
import { useShiftPredictionWarnings } from "../../utils/warningDialogs/useShiftPredictionWarnings";
import { isAgencyNurse } from "../../utils/warningDialogs/WarningDialog";
import { DailyGapsQuickAction } from "../DayViewGaps/DailyRow/NurseInShiftTrailingNode/utils";
import type { NormalizedNurseMenuProps } from "./NormalizedNurseMenuTypes";
import { useUpdateLocationAndHoursForShift } from "./useUpdateLocationAndHours";

export const useNormalizedNurseMenu = ({
	nurseInShift,
	onClose,
	currentQuickAction,
}: Omit<NormalizedNurseMenuProps, "source" | "allowEditLocationAndHours" | "displayProfileButton" | "menuAnchorElement" | "showActual">) => {
	const { chosenDepartment } = useChosenDepartment();
	const { getNurseById } = useRosterLookup(chosenDepartment.department_id);
	const [nestedPopoverOpen, setNestedPopoverOpen] = useState(false);

	const [editLocationAndHoursOpen, setEditLocationAndHoursOpen] = useState(
		currentQuickAction === DailyGapsQuickAction.LOCATION || currentQuickAction === DailyGapsQuickAction.TIME,
	);

	const handleOpenEditLocationAndHoursClick = useCallback((event?: MouseEvent<HTMLElement>) => {
		event?.stopPropagation();
		setEditLocationAndHoursOpen((prevState) => !prevState);
	}, []);

	const editLocationAndHoursOpenRef = useRef(editLocationAndHoursOpen);
	useEffect(() => {
		editLocationAndHoursOpenRef.current = editLocationAndHoursOpen;
	}, [editLocationAndHoursOpen]);

	const handleCloseNurseMenu = useCallback(
		(shiftDeleted: boolean) => {
			if (!editLocationAndHoursOpenRef.current) {
				onClose?.(shiftDeleted);
			}
		},
		[onClose],
	);

	const { updateLocationAndHours, updateNurseLocationOrTimesLoading } = useUpdateLocationAndHoursForShift();

	// Pending edit is held in a ref, not state: the warning cascade may resolve synchronously
	// (no warnings fired), in which case `performSave` runs in the same tick as the click handler
	// — before any setState would have re-rendered. A ref always reads the latest write.
	const pendingUpdateRef = useRef<NurseInShift | null>(null);

	const performSave = useCallback(async () => {
		const pending = pendingUpdateRef.current;
		if (!pending) return;
		if (
			await updateLocationAndHours({
				originalNurseInShift: nurseInShift,
				updatedNurseInShift: pending,
			})
		) {
			onClose?.(false);
		}
		pendingUpdateRef.current = null;
	}, [nurseInShift, onClose, updateLocationAndHours]);

	const { warningState, runChecks, handleAgencyConfirm, handleOvertimeConfirm, handleDailyHoursConfirm, handleCancelWarnings, handlePtoConfirm } =
		useShiftPredictionWarnings(performSave);

	// Derived from warningState — never out of sync. While any warning dialog is open, treat the
	// edit popover as having a "nested popover" and ignore click-outside; otherwise the click on
	// the dialog (which lives outside the popover's DOM subtree) tears down the menu before the
	// cascade can finish.
	const anyWarningOpen =
		warningState.isPtoWarningOpen || warningState.isAgencyWarningOpen || warningState.isOvertimeWarningOpen || warningState.isDailyHoursWarningOpen;

	const handleCloseEditLocationAndHoursClick = useCallback(() => {
		if (!editLocationAndHoursOpen || nestedPopoverOpen || anyWarningOpen) {
			return;
		}
		setEditLocationAndHoursOpen(false);
	}, [editLocationAndHoursOpen, nestedPopoverOpen, anyWarningOpen]);

	const handleSaveLocationAndHours = useCallback(
		async (updatedNurseInShift: NurseInShift) => {
			pendingUpdateRef.current = updatedNurseInShift;

			const nurse = getNurseById(updatedNurseInShift.nurseId);
			// PTO is gated by EditHoursAndLocation's local dialog before this fires; passing
			// hasPto: false keeps the shared cascade focused on Agency/OT/DailyHours.
			await runChecks({
				nurseId: updatedNurseInShift.nurseId,
				startTime: updatedNurseInShift.startTime,
				endTime: updatedNurseInShift.endTime,
				isGlobalSalary: nurse?.isGlobalSalary,
				isAgency: isAgencyNurse(updatedNurseInShift.type),
				hasPto: false,
				originalShiftId: nurseInShift.id,
			});
		},
		[getNurseById, runChecks, nurseInShift.id],
	);

	return {
		updateNurseLocationOrTimesLoading,
		handleSaveLocationAndHours,
		editLocationAndHoursOpen,
		handleCloseNurseMenu,
		handleOpenEditLocationAndHoursClick,
		handleCloseEditLocationAndHoursClick,
		setNestedPopoverOpen,
		warningState,
		handleAgencyConfirm,
		handleOvertimeConfirm,
		handleDailyHoursConfirm,
		handleCancelWarnings,
		handlePtoConfirm,
		longShiftDuration: chosenDepartment.longShiftDuration,
	};
};
